import { ConversionFactorUnit } from '../unit.model';
import {
  ElementCategoryID,
  ProductCategoryId,
} from '../element-categories.model';
import { IElementProperty } from '../element-property.model';
import { ElementQuantityRecord } from '../element-quantities.model';
import type { ElementOriginMapping } from '../element-import.model';
import type { ProposalId } from '../proposals.model';
import { RecipeID } from '../recipe.model';
import { ProductID } from '../product.model';
import { Replace } from '../type-helpers.model';
import { ElementKind } from './element-kind.model';
import { IElementID } from './ids.model';
import { ExpressionValue } from './expression-value.model';

/**
 * Shared properties between all elements
 */
export interface IBaseElement {
  id: IElementID;
  kind: ElementKind;
}

/**
 * Reference to a Recipe
 */
export interface IRecipeRef {
  recipe_id: RecipeID;
}

/**
 * Reference to an Element Category
 */
export interface IElementCategoryRef {
  category_id: ElementCategoryID;
}

/**
 * What materialized an imported element. `'auto'` = batch auto-import at file
 * drop (no user interaction); `'user'` = the user drove it (in-row mapping,
 * side-panel save); `'ai'` = an applied AI mapping suggestion (DEV-3605).
 */
export type CsvImportTrigger = 'auto' | 'user' | 'ai';

/**
 * Provenance pointer on a real element back to the candidate row it was
 * materialized from (DEV-3298). `importId` / `candidateRowId` are DB ids --
 * NOT element ids, so they are never touched by the factory idMap remap.
 */
export type ElementOrigin = {
  kind: 'csvImport';
  importId: string;
  candidateRowId: string;
  /**
   * Hash of the materialization mapping (the same object whether held inline or
   * promoted to the resource-code table). Lets a later check tell whether the
   * mapping that produced this element still matches; drift is recorded, not acted on.
   */
  mappingHash: string;
  /**
   * What materialized this element, see {@link CsvImportTrigger}. Drives the
   * AUTO-MAPPED vs MAPPED vs MAPPED AI-SUGGESTED status split. Missing on
   * legacy elements is treated as `'auto'`.
   */
  trigger?: CsvImportTrigger;
} & (
  | {
      /** Mapping not yet saved to the resource-code table; full snapshot held inline. */
      source: 'inline';
      mapping: ElementOriginMapping;
    }
  | {
      /** Mapping promoted to the table; resolved from the row's code + unitFrom at read time. */
      source: 'stored';
      scope: 'org' | 'project';
    }
);

/**
 * Main element type
 */
export interface IElement
  extends IBaseElement, Partial<IRecipeRef>, Partial<IElementCategoryRef> {
  kind: ElementKind.Element;
  name: string;

  elements: OneOfElementListChildren[];
  // TODO: Make sure this always exist by migrating DB
  properties?: IElementProperty[];

  quantity?: ElementQuantityRecord;

  selectedQuantity?: keyof ElementQuantityRecord;

  fallbackName?: string;

  /**
   * If this is the active version of the element.
   * The rest should be hidden and excluded from calculations
   */
  isActiveVersion?: boolean;

  /**
   * User-controlled: when true, the element is disabled (hidden and excluded from calculations).
   * Can be toggled on/off by the user via the visibility/Enable-Disable controls.
   */
  isDeactivated?: boolean;

  /**
   * Proposal-scoped delete marker. When true, the element stays in baseline but is hidden
   * from the current resolved view and excluded from calculations.
   */
  isDeleted?: boolean;

  /**
   * Set when the element was added while this proposal was selected: it lives in
   * baseline deactivated and is activated by that proposal only. Lets a delete in
   * the same proposal remove the element for real instead of leaving an orphan
   * behind (DEV-3702). A user-deactivated baseline element never carries it.
   * Cleared when the element becomes real baseline data (new baseline from proposal).
   */
  createdInProposalId?: ProposalId;

  /**
   * System parent created to hold one SBEF code's imported rows (DEV-3848).
   * Absent on groups the user built themselves, even when those groups contain
   * imported children and carry the same code.
   */
  importReplaceParent?: true;

  /**
   * Provenance: set when this element was materialized from a CSV import
   * candidate row (DEV-3298). Undefined for manually created elements.
   */
  origin?: ElementOrigin;
}

export type ProductCategoryElement = Replace<
  IElement,
  { category_id: ProductCategoryId }
>;

export type IElementVersion = IElement;

/**
 * Element with reference to a recipe
 */
export type IRecipeElement = IElement & IRecipeRef;

export interface IProductElement extends IBaseElement {
  kind: ElementKind.Product;

  /** Generic or EPD product id */
  product_id: ProductID;

  /** If the product is an EPD, the generic product id is stored here. Else it will be undefined */
  generic_product_id: ProductID | undefined;

  /**
   * Count of the product. IE how much of the product is used (absolute values now)
   */
  count: ExpressionValue;

  /**
   * Unit of the product.
   * Currently only conversion factor units are allowed but should be allowed to be other units as well
   */
  unit: ConversionFactorUnit;

  /**
   * If true this property will be excluded from the sum if it doesn't have a count
   */
  hide_product_without_count?: boolean;

  /**
   * Name of the product. Just for override from Product categories
   */
  name?: string;

  /**
   * Overrides for product element conversion factors.
   * - Spill ({@link ProductElementOverrideTarget.WASTE_PERCENT}) and transport ({@link ProductElementOverrideTarget.TRANSPORT_KM}):
   *   cascading — apply to both EPD and generic product.
   * - Co2e ({@link ProductElementOverrideTarget.CO2E_A1_A3}) and cost ({@link ProductElementOverrideTarget.SEK_A1_A3}):
   *   genericOnly — apply only to the generic product.
   */
  productOverrides?: {
    [ProductElementOverrideTarget.CO2E_A1_A3]?: ScopedOverride<
      'genericProduct',
      OverrideTypes['marketPercentileMultiplier']
    >;
    [ProductElementOverrideTarget.SEK_A1_A3]?: ScopedOverride<
      'genericProduct',
      OverrideTypes['marketPercentileMultiplier']
    >;
    [ProductElementOverrideTarget.TRANSPORT_KM]?: ScopedOverride<
      'all',
      OverrideTypes['replace']
    >;
    [ProductElementOverrideTarget.WASTE_PERCENT]?: ScopedOverride<
      'all',
      OverrideTypes['replace']
    >;
  };
}

type OverrideTypes = {
  /** Replaces the property value entirely with the given value */
  replace: { type: 'replace'; value: number };
  /**
   * Multiplies the property value by a factor derived from market data.
   * The stored value is a percentile (0-100) within the product's market distribution.
   * At calculation time: percentile → factor (via product's market estimate range) → multiplier.
   */
  marketPercentileMultiplier: {
    type: 'marketPercentileMultiplier';
    value: number;
  };
};

export type ProductElementOverrideScope = 'all' | 'genericProduct';

export type ScopedOverride<
  S extends ProductElementOverrideScope,
  O extends ProductElementOverride,
> = { applicationScope: S; override: O };

export type ProductElementOverrides = NonNullable<
  IProductElement['productOverrides']
>;
export type ProductElementOverride = OverrideTypes[keyof OverrideTypes];
export enum ProductElementOverrideTarget {
  CO2E_A1_A3 = 'CO2E_A1_A3',
  SEK_A1_A3 = 'SEK_A1_A3',
  TRANSPORT_KM = 'TRANSPORT_KM',
  WASTE_PERCENT = 'WASTE_PERCENT',
}

export type IProductReference = Pick<
  IProductElement,
  'product_id' | 'generic_product_id'
>;

export type OneOfElementListChildren = IElement | IProductElement;
